import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DRAWER_ACTION_STYLE,
  DRAWER_ARROW_BUTTON_STYLE,
  DRAWER_HEADER_STYLE,
  DRAWER_TITLE_STYLE,
  DrawerArrowIcon,
  DrawerHeaderActions,
} from "./ui/drawerChrome";

const PDFJS_ASSET_VERSION = "4.7.76-matematron-1";

function UniDrawer({ isOpen, onClose, width, title, headerRight, showBackdrop = true, children }) {
  const rootRef = useRef(null);
  if (!rootRef.current && typeof document !== "undefined") {
    const el = document.createElement("div");
    el.id = "formula-drawer-root";
    document.body.appendChild(el);
    rootRef.current = el;
  }
  const root = rootRef.current;
  if (!root) return null;

  const overlay = showBackdrop ? (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.40)",
        transition: "opacity 200ms ease",
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "auto" : "none",
        zIndex: 10000,
      }}
    />
  ) : null;

  const panel = (
    <aside
      role="dialog"
      aria-modal={showBackdrop ? "true" : "false"}
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        right: isOpen ? 0 : -width,
        width,
        maxWidth: "100vw",
        background: "var(--color-bg-strong, rgba(15,15,15,.85))",
        backdropFilter: "blur(10px) saturate(1.15)",
        WebkitBackdropFilter: "blur(10px) saturate(1.15)",
        color: "var(--color-text, #eaeaea)",
        boxShadow: isOpen ? "0 10px 25px rgba(0,0,0,.35)" : "none",
        display: "flex",
        flexDirection: "column",
        transition: "right 240ms ease",
        zIndex: 10001,
      }}
    >
      <header
        style={DRAWER_HEADER_STYLE}
      >
        <div
          style={{ ...DRAWER_TITLE_STYLE, textAlign: "left" }}
        >
          {title}
        </div>

        {headerRight ? <DrawerHeaderActions>{headerRight}</DrawerHeaderActions> : null}

        <button
          type="button"
          onClick={onClose}
          className="btn-secondary"
          title="Schowaj (Esc)"
          aria-label="Schowaj panel"
          style={DRAWER_ARROW_BUTTON_STYLE}
        >
          <DrawerArrowIcon direction="right" />
        </button>
      </header>

      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>{children}</div>
    </aside>
  );

  return createPortal(
    <>
      {overlay}
      {panel}
    </>,
    root
  );
}

export default function FormulaSheet({
  isOpen,
  onOpenChange,
  width = 740,
  pdfPath = "/assets/wybrane_wzory_matematyczne_EM2023.pdf",
  headerRightExtra = null,
  showBackdrop = true,
}) {
  const absolutePdfUrl = useMemo(() => {
    try {
      return new URL(pdfPath, window.location.origin).toString();
    } catch {
      return pdfPath;
    }
  }, [pdfPath]);

  const viewerUrl = useMemo(() => {
    const fileParam = encodeURIComponent(absolutePdfUrl);
    return `/pdfjs/web/viewer.html?v=${PDFJS_ASSET_VERSION}&file=${fileParam}#zoom=page-width`;
  }, [absolutePdfUrl]);

  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activated, setActivated] = useState(false);
  const timerRef = useRef(null);
  const everLoadedRef = useRef(false);
  const openRunIdRef = useRef(0);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = (runId) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (openRunIdRef.current !== runId) return;
      if (!everLoadedRef.current) setFailed(true);
    }, 12000);
  };

  useEffect(() => {
    if (!isOpen) return;
    setActivated(true);
    const runId = (openRunIdRef.current = openRunIdRef.current + 1);
    setFailed(false);
    if (everLoadedRef.current) clearTimer();
    else startTimer(runId);
    return clearTimer;
  }, [isOpen, reloadKey]);

  const handleIframeLoad = () => {
    everLoadedRef.current = true;
    clearTimer();
    setFailed(false);
  };

  const handleIframeError = () => {
    clearTimer();
    setFailed(true);
  };

  const retry = () => {
    everLoadedRef.current = false;
    setFailed(false);
    setReloadKey((k) => k + 1);
  };

  const openInNewTab = (
    <a
      href={absolutePdfUrl}
      target="_blank"
      rel="noreferrer"
      className="btn-secondary"
      style={{ ...DRAWER_ACTION_STYLE, display: "inline-flex", alignItems: "center", textDecoration: "none" }}
    >
      Otwórz w nowej karcie
    </a>
  );

  const headerRight = (
    <>
      {headerRightExtra}
      {openInNewTab}
    </>
  );

  return (
    <UniDrawer
      isOpen={isOpen}
      onClose={() => onOpenChange?.(false)}
      width={width}
      title="Karta wzorów (CKE)"
      headerRight={headerRight}
      showBackdrop={showBackdrop}
    >
      {activated && !failed ? (
        <iframe
          key={reloadKey}
          title="Wybrane wzory matematyczne CKE"
          src={viewerUrl}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            display: isOpen ? "block" : "none",
            background: "transparent",
          }}
          allow="fullscreen; allowfullscreen"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      ) : null}

      {isOpen && failed ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            padding: 24,
            textAlign: "center",
          }}
        >
          <div>
            <p style={{ opacity: 0.9, marginBottom: 14 }}>Nie udało się wczytać podglądu PDF.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={retry}
                style={{ padding: "6px 10px", borderRadius: 10 }}
              >
                Spróbuj ponownie
              </button>
              {openInNewTab}
            </div>
          </div>
        </div>
      ) : null}
    </UniDrawer>
  );
}
